import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import photoGroups from './photoData';
import Footer from '../../components/Footer';

const GalleryContainer = styled.div`
  background: linear-gradient(135deg, #ff0000 0%, #0000ff 100%);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex-grow: 1;
  margin-top: 16vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;

  @media (max-width: 768px) {
    margin-top: 12vh;
    padding: 10px;
  }
`;

const GalleryGroup = styled.div`
  margin: 40px 0;
  text-align: center;
  width: 100%;
  max-width: 1200px;
  position: relative;
`;

const GroupTitle = styled.h2`
  margin-bottom: 10px;
  color: white;
  font-size: 2rem;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.5em;
  }
`;

const GroupDescription = styled.p`
  margin-bottom: 30px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  line-height: 1.6;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 0.9em;
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
  padding: 20px 0;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
  }
`;

const GalleryItem = styled.div`
  position: relative;
  cursor: pointer;
  overflow: hidden;
  border-radius: 12px;
  aspect-ratio: 3/2;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const LoadMoreButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  color: #ff7e5f;
  border: none;
  padding: 12px 30px;
  margin: 30px 0;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 25px;
  transition: all 0.3s;

  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.direction === 'left' ? 'left: 20px;' : 'right: 20px;'}
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
`;

const TimelineSeparator = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 40px 0;
`;

const TimelineDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: white;
  margin: 10px 0;
`;

const TimelineLine = styled.div`
  width: 2px;
  height: 80px;
  background: white;

  @media (max-width: 768px) {
    height: 60px;
  }
`;

const FooterWrapper = styled.div`
  background-color: black;
  color: white;
  width: 100%;
`;

const Gallery = () => {
  const [visibleCounts, setVisibleCounts] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentGroupImages, setCurrentGroupImages] = useState([]);
  const location = useLocation();
  const groupRefs = useRef([]);

  useEffect(() => {
    const initialCounts = {};
    photoGroups.forEach((_, index) => {
      initialCounts[index] = 12;
    });
    setVisibleCounts(initialCounts);
  }, []);

  useEffect(() => {
    if (location.state?.scrollToIndex !== undefined && groupRefs.current[location.state.scrollToIndex]) {
      setTimeout(() => {
        groupRefs.current[location.state.scrollToIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [location]);

  const loadMore = (groupIndex) => {
    setVisibleCounts(prev => ({
      ...prev,
      [groupIndex]: prev[groupIndex] + 12
    }));
  };

  const openModal = (image, groupImages, index) => {
    setSelectedImage(image);
    setCurrentGroupImages(groupImages);
    setCurrentIndex(index);
  };

  const closeModal = () => {
    setSelectedImage(null);
    setCurrentGroupImages([]);
  };

  const nextImage = () => {
    const newIndex = (currentIndex + 1) % currentGroupImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(currentGroupImages[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentIndex - 1 + currentGroupImages.length) % currentGroupImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(currentGroupImages[newIndex]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'ArrowLeft') prevImage();
    if (e.key === 'Escape') closeModal();
  };

  useEffect(() => {
    if (selectedImage) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedImage, currentIndex]);

  return (
    <GalleryContainer>
      <ContentWrapper>
        {photoGroups.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            <GalleryGroup ref={el => groupRefs.current[groupIndex] = el}>
              <GroupTitle>{group.title}</GroupTitle>
              <GroupDescription>{group.description}</GroupDescription>
              <ImageGrid>
                {group.images.slice(0, visibleCounts[groupIndex] || 12).map((photo, index) => (
                  <GalleryItem key={photo.id} onClick={() => openModal(photo, group.images, index)}>
                    <img src={photo.src} alt={photo.alt} loading="lazy" />
                  </GalleryItem>
                ))}
              </ImageGrid>
              {group.images.length > (visibleCounts[groupIndex] || 12) && (
                <LoadMoreButton onClick={() => loadMore(groupIndex)}>
                  Load More ({group.images.length - (visibleCounts[groupIndex] || 12)} remaining)
                </LoadMoreButton>
              )}
            </GalleryGroup>
            {groupIndex < photoGroups.length - 1 && (
              <TimelineSeparator>
                <TimelineDot />
                <TimelineLine />
                <TimelineDot />
              </TimelineSeparator>
            )}
          </React.Fragment>
        ))}
      </ContentWrapper>

      <FooterWrapper>
        <Footer />
      </FooterWrapper>

      {selectedImage && (
        <Modal onClick={closeModal}>
          <CloseButton onClick={closeModal}>×</CloseButton>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <NavButton direction="left" onClick={prevImage}>‹</NavButton>
            <ModalImage src={selectedImage.src} alt={selectedImage.alt} />
            <NavButton direction="right" onClick={nextImage}>›</NavButton>
            <ImageCounter>{currentIndex + 1} / {currentGroupImages.length}</ImageCounter>
          </ModalContent>
        </Modal>
      )}
    </GalleryContainer>
  );
};

export default Gallery;
